import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { api } from '../../api/client';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import { OpportunityStageBadge } from '../sales/OpportunityStageBadge';
import { OpportunityFormDialog } from '../sales/OpportunityFormDialog';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { unwrapArray } from '../../lib/api-envelope';
import { formatCalendarDate, formatInstant } from '../../lib/calendar-date';
import { fmtCurrency } from '../../lib/display-labels';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { salesActivityKindLabel } from '../../lib/sales-activity';
import { nextStepOverdue } from '../../lib/opportunity';
import { OPEN_STAGES, type Opportunity } from '../../types/opportunity';
import type { SalesActivity } from '../../types/sales-activity';

/**
 * Meetings and Opportunities on the customer page (CRM-FE-4).
 *
 * Meetings: the customer's CONFIRMED meetings (the list never asks for Scout
 * drafts), newest first as the API orders them; the last 10, then "View all"
 * shows the rest of the same answer. Opportunities: open first, then won and
 * lost — a reordering of the rows the API returned, nothing computed. Each
 * open deal shows its next step and due day, with Overdue when the due day is
 * before the tenant's today (lib/opportunity nextStepOverdue).
 * The customers.notes blob is untouched and stays where it is.
 */
const MEETINGS_SHOWN = 10;

function Panel({ id, title, action, children }: { id: string; title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section aria-labelledby={id} className="rounded-xl border border-fieldiq-border bg-fieldiq-card p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 id={id} className="text-xs font-bold uppercase tracking-wider text-white">{title}</h3>
        {action}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

export function CustomerMeetingsPanel({ customerId }: { customerId: string }) {
  const timeZone = useTenantTimeZone();
  const [all, setAll] = useState(false);
  const q = useQuery({
    queryKey: ['sales-activities', 'list', { customer_id: customerId }],
    queryFn: () => api.salesActivities.list({ customer_id: customerId }).then((r) => unwrapArray<SalesActivity>(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(q);
  const rows = failure ? undefined : q.data;
  const shown = rows ? (all ? rows : rows.slice(0, MEETINGS_SHOWN)) : [];

  return (
    <Panel
      id="customer-meetings-title"
      title="Meetings"
      action={
        <Link to={`/sales/meetings/new?customer=${encodeURIComponent(customerId)}`} className="inline-flex items-center gap-1 text-sm font-semibold text-fieldiq-accent-signal">
          <Plus size={14} aria-hidden="true" /> Log meeting
        </Link>
      }
    >
      {failure ? (
        <LoadFailureNotice error={queryFailureError(q)} subject="meetings" className="py-6" />
      ) : !rows ? (
        <div className="h-16 animate-pulse rounded-lg bg-fieldiq-dark/60" aria-busy="true" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-fieldiq-muted">No meetings logged yet</p>
      ) : (
        <>
          <ul className="divide-y divide-fieldiq-border">
            {shown.map((m) => (
              <li key={m.id} className="py-2 text-sm">
                <Link to={`/sales/meetings/${m.id}`} className="flex flex-wrap items-baseline justify-between gap-2 hover:underline">
                  <span className="text-white">{formatInstant(m.occurredAt, timeZone) ?? '—'} · {salesActivityKindLabel(m.kind)}</span>
                  <span className="text-fieldiq-muted">{m.ownerName ?? '—'}</span>
                </Link>
                {m.nextStep && <p className="text-fieldiq-muted">{m.nextStep}</p>}
              </li>
            ))}
          </ul>
          {rows.length > MEETINGS_SHOWN && (
            <button type="button" onClick={() => setAll((a) => !a)} className="mt-2 text-sm font-semibold text-fieldiq-text hover:underline">
              {all ? 'Show the latest 10' : `View all ${rows.length}`}
            </button>
          )}
        </>
      )}
    </Panel>
  );
}

export function CustomerOpportunitiesPanel({ customer }: { customer: { id: string; name: string; status?: string | null } }) {
  const timeZone = useTenantTimeZone();
  const [creating, setCreating] = useState(false);
  // Memoised: the dialog re-seeds its fields whenever this object changes, so a
  // fresh literal per render would wipe what the reader is typing.
  const initialCustomer = useMemo(
    () => ({ id: customer.id, name: customer.name, status: customer.status }),
    [customer.id, customer.name, customer.status],
  );
  const q = useQuery({
    queryKey: ['opportunities', 'list', { customer_id: customer.id }],
    queryFn: () => api.opportunities.list({ customer_id: customer.id }).then((r) => unwrapArray<Opportunity>(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(q);
  const rows = failure ? undefined : q.data;
  const isOpen = (o: Opportunity) => (OPEN_STAGES as readonly string[]).includes(o.stage);
  const ordered = rows ? [...rows.filter(isOpen), ...rows.filter((o) => !isOpen(o))] : [];

  return (
    <Panel
      id="customer-opportunities-title"
      title="Opportunities"
      action={
        <button type="button" onClick={() => setCreating(true)} className="inline-flex items-center gap-1 text-sm font-semibold text-fieldiq-accent-signal">
          <Plus size={14} aria-hidden="true" /> New opportunity
        </button>
      }
    >
      {failure ? (
        <LoadFailureNotice error={queryFailureError(q)} subject="opportunities" className="py-6" />
      ) : !rows ? (
        <div className="h-16 animate-pulse rounded-lg bg-fieldiq-dark/60" aria-busy="true" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-fieldiq-muted">No opportunities yet</p>
      ) : (
        <ul className="divide-y divide-fieldiq-border">
          {ordered.map((o) => (
            <li key={o.id} className="py-2 text-sm">
              <Link to={`/sales/pipeline/${o.id}`} className="flex flex-wrap items-center justify-between gap-2 hover:underline">
                <span className="font-semibold text-white">{o.title}</span>
                <OpportunityStageBadge stage={o.stage} />
              </Link>
              <p className="text-fieldiq-muted">
                {fmtCurrency(o.estimatedValue, o.currency)} · {o.contactName ?? 'No contact'} · close {formatCalendarDate(o.expectedCloseDate) ?? '—'}
              </p>
              {isOpen(o) && (o.nextStep || o.nextStepDue) && (
                <p className="flex flex-wrap items-center gap-2 text-fieldiq-text">
                  <span className="min-w-0">Next: {o.nextStep ?? '—'} · due {formatCalendarDate(o.nextStepDue) ?? '—'}</span>
                  {nextStepOverdue(o, timeZone) === true && (
                    <span className="rounded-fi-pill border border-black/20 bg-fi-signal-red px-2 py-0.5 text-[0.62rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink">Overdue</span>
                  )}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      <OpportunityFormDialog
        open={creating}
        onClose={() => setCreating(false)}
        initialCustomer={initialCustomer}
      />
    </Panel>
  );
}
